/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ActivityRestService,
  BuilderPermission,
  ActivityDetails,
  SalesFolderStatus,
} from '@gh/builder-shared-data';
import {
  DateTimeTypes,
  dictionaryItemFromDto, DictionaryService,
  FormStateSnapshot,
  globalDictionaries,
  queryFormMode,
  State,
  UserContext,
} from '@gh/common-shared';
import {
  DialogService,
  FormMode,
  isEditFormMode,
  NotificationService,
  STYLES_FLEX_ROW_LAYOUT,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$, map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  LeadSalesFolder,
  LeadSalesFolderMeeting,
  LeadSfJobDetails,
} from '../../../resources/lead.model';
import { LeadDetailsCommandService } from '../lead-details-command.service';
import {
  LeadDetailsState,
  queryCurrentSalesFolderId,
  queryLeadCurrentSalesFolder,
  queryLeadCurrentSalesFolderJobDetails,
  queryLeadCurrentSalesFolderMemo,
  queryLeadCurrentSalesFolderStatus,
  queryLeadIsCurrentSalesFolderNew,
  queryLeadIsDppAllowed,
  queryLeadSalesFolderInEditing,
  queryLeadIsSalesFolderSelected,
} from '../lead-details.state';
import { SfMeetingGridDefinition } from './sf-meeting-grid-definition.service';
import { ID } from '@gh/common-shared-data';
import { LeadDetailsBusinessService } from '../../../resources/lead-details-business.service';
import { UTCToLocal } from '@gh/core-util';
import { StageAdvancementNotificationService } from '@gh/builder-shared';

@Component({
  standalone: false,
  selector: 'gh-lead-details-sf-content',
  templateUrl: 'lead-details-sf-content.component.html',
  styles: [STYLES_FLEX_ROW_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeadDetailsSfContentComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() currentSalesFolderId: ID;
  generalSnapshot$: Observable<FormStateSnapshot<LeadSalesFolder>>;
  salesFolderInEditing$: Observable<boolean>;
  hasSelectedSalesFolders$: Observable<boolean>;
  jobDetails$: Observable<Maybe<LeadSfJobDetails>>;
  activity: LeadSalesFolderMeeting[];
  memo$: Observable<string>;
  hasDpp$: Observable<boolean>;
  salesFolderCreateMode$: Observable<boolean>;
  isDppAllowed$: Observable<boolean>;
  canCreateDpp$: Observable<boolean>;
  isInit: boolean = true;
  activityTypes: any[];
  constructor(private state: State<LeadDetailsState>,
              private userContext: UserContext,
              private commands: LeadDetailsCommandService,
              private leadService: LeadDetailsBusinessService,
              private dialogService: DialogService,
              private dictionaryService: DictionaryService,
              private activityService: ActivityRestService,
              private changeDetection: ChangeDetectorRef,
              private notifications: NotificationService,
              public meetingGridDefinition: SfMeetingGridDefinition,
              private stageAdvancementNotifications: StageAdvancementNotificationService) {

  }
  get formMode(): FormMode {
    return this.state.project(queryFormMode);
  }
  get editMode(): boolean {
    return isEditFormMode(this.formMode);
  }

  ngOnInit(): void {
    let isInit = true;
    this.generalSnapshot$ = this.state.select(queryLeadCurrentSalesFolder).pipe(
      filter$(Boolean),
      map$((x: any) => x?.general));
    this.hasSelectedSalesFolders$ = this.state.select(queryLeadIsSalesFolderSelected);
    this.salesFolderInEditing$ = this.state.select(queryLeadSalesFolderInEditing);
    this.jobDetails$ = this.state.select(queryLeadCurrentSalesFolderJobDetails);
    this.memo$ = this.state.select(queryLeadCurrentSalesFolderMemo);
    this.hasDpp$ = this.jobDetails$.pipe(map$((jobDetails) => jobDetails && jobDetails.isDpp || false));
    this.salesFolderCreateMode$ = this.state.select(queryLeadIsCurrentSalesFolderNew);
    this.isDppAllowed$ = this.state.select(queryLeadIsDppAllowed);
    this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.activityType).subscribe((activityTypes) => {
      this.activityTypes = activityTypes ? activityTypes.map(dictionaryItemFromDto) : [];
      if (this.currentSalesFolderId && !this.currentSalesFolderId.includes('sf:')) {
        this.activityService.getList(this.currentSalesFolderId).subscribe(
          (activityList) => {
            this.setActivityList(activityList, isInit);
            isInit = false;
          },
          (error) => {
            this.setActivityList([], isInit);
            isInit = false;
            this.notifications.processAll(error);
          });
      } else {
        this.setActivityList([], isInit);
        isInit = false;
      }

    });
    this.canCreateDpp$ = this.state.select(queryLeadCurrentSalesFolderStatus).pipe(
      map$((status) =>
        status === SalesFolderStatus.Active
        && this.userContext.hasPermission(BuilderPermission.JobAdmin.Jobs.CREATE_DPP)));
  }

  ngOnChanges({ currentSalesFolderId }: SimpleChanges): void {
    if (!!currentSalesFolderId.previousValue && currentSalesFolderId.currentValue !== currentSalesFolderId.previousValue) {
      if (currentSalesFolderId.currentValue.includes('sf:')) {
        this.setActivityList([]);
        return;
      }
      this.activityService.getList(this.currentSalesFolderId).subscribe(
        (activityList) => this.setActivityList(activityList || []),
        (error) => {
          this.setActivityList([]);
          this.notifications.processAll(error);
        });
    }
  }

  ngAfterViewInit(): void {
    this.isInit = false;
  }

  onMeetingAdd(): void {
    const sf = this.state.project(queryLeadCurrentSalesFolder);
    if (sf) {
      this.commands.meetingAdd(sf.id, () => this.addToActivityList());
    }
  }

  onMeetingOpen(meeting: LeadSalesFolderMeeting): void {
    const sf = this.state.project(queryLeadCurrentSalesFolder);
    if (sf) {
      this.commands.meetingOpen(sf.id, meeting,
                                () => this.addToActivityList());
    }
  }

  onMeetingDelete(meeting: LeadSalesFolderMeeting): void {
    const sfId = this.state.project(queryCurrentSalesFolderId);

    if (sfId) {
      this.dialogService.confirmation(yesNoConfirmation('msg_sf_mark_activity_deleted',
                                                        { date: DateTimeTypes.DateTime.format(UTCToLocal(meeting.startDate))})).pipe(
        filter$(yesAnswer))
        .subscribe(() => {
          this.activityService.delete(sfId, meeting.id || '').subscribe((res: ActivityDetails[]) => {
            this.setActivityList(res);
          },                                                            this.notifications.processAll);
        });
    }
  }

  onMeetingUnDelete(meeting: LeadSalesFolderMeeting): void {
    const sfId = this.state.project(queryCurrentSalesFolderId);

    if (sfId) {
      this.dialogService.confirmation(yesNoConfirmation('msg_sf_undelete_activity',
                                                        { date: DateTimeTypes.DateTime.format(UTCToLocal(meeting.startDate))})).pipe(
        filter$(yesAnswer))
        .subscribe(() => {
          this.activityService.changeStatus(sfId, meeting.id || '').subscribe((res: ActivityDetails[]) => {
            this.setActivityList(res);
          },                                                                  this.notifications.processAll);
        });
    }
  }

  onCurrentSalesFolderChange(snapshot: FormStateSnapshot<LeadSalesFolder>): void {
    this.commands.setCurrentSalesFolder(snapshot, this.isInit);
    this.isInit = false;
  }

  onMemoChange(memo: string): void {
    this.commands.setMemo(memo);
  }

  onCreateDppJob(): void {
    const sfId = this.state.project(queryCurrentSalesFolderId);
    if (sfId) {
      this.commands.createDppJob(sfId);
    }
  }

  openRfqManagement(): void {
    const sfId = this.state.project(queryCurrentSalesFolderId);
    if (sfId) {
      this.commands.openRfqManagement(sfId);
    }
  }

  onMarkAsDone(meeting: LeadSalesFolderMeeting): void {
    this.setStatus(meeting.id || '', true);
  }

  onUnMarkAsDone(meeting: LeadSalesFolderMeeting): void {
    this.setStatus(meeting.id || '', false);
  }

  activityChange(): void {
    this.addToActivityList();
  }

  private setStatus(meetingId: ID, status: boolean): void {
    const sfId = this.state.project(queryCurrentSalesFolderId);
    if (sfId) {
      this.activityService.setState(sfId, meetingId, status).subscribe((res) => {
        if (status) {
          const previous = (this.activity || []).find((item) => item.id === meetingId);
          const saved = (res || []).find((item) => item.id === meetingId) || previous;
          this.stageAdvancementNotifications.notifyAfterActivityMarkedDone(sfId, {
            ...saved,
            isDone: true,
            activityType: saved?.activityType ?? previous?.activityType,
            milestoneEvent: saved?.milestoneEvent ?? previous?.milestoneEvent,
          }, !!previous?.isDone).subscribe();
          this.dialogService.confirmation(yesNoConfirmation('msg_activity_marked_as_done')).pipe(
          filter$(yesAnswer))
          .subscribe(() => this.onMeetingAdd());
        }
        this.setActivityList(res);
      },                                                               this.notifications.processAll);
    }
  }

  private setActivityList(activityList: ActivityDetails[], isInit?: boolean): void {
    if (!isInit) {
      this.leadService.activityWasChanges.next('');
    }
    this.activity = activityList.map((item) =>
                                       ({
                                         ...item,
                                         activityType: item.activityType ? this.activityTypes.find(
                                             (type) => type.id === item.activityType.toString())?.name : '',
                                         guests: item.guests || [],
                                       }));
    this.changeDetection.markForCheck();
  }

  private addToActivityList(): void {
    this.leadService.activityWasChanges.next('');
    this.activityService.getList(this.currentSalesFolderId).subscribe((res) => this.setActivityList(res));
    this.changeDetection.markForCheck();
  }
}
